import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import JSZip from 'jszip';
import html2canvas from 'html2canvas';
import { 
  ArrowLeft, Download, Zap, TrendingUp, AlertTriangle, 
  Calendar, BarChart3, Trash2, Building2, CalendarDays, FileText 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, ReferenceLine, Legend 
} from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { usePiekanalyses } from '@/hooks/usePiekanalyses';
import { 
  DayViewerDialog, 
  DayCompareDialog, 
  YearCalendar, 
  SolutionSettingsCard,
  generateConsumptionReportPDF 
} from '@/components/piekanalyse';
import type { PiekAnalyse } from '@/types/piekanalyse';
import { Info } from 'lucide-react';

const DUTCH_MONTHS = [
  'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
  'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'
];

const DUTCH_MONTH_ABBR = [
  'Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun',
  'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'
];

// Generate Y-axis ticks dynamically based on max value
const generateYTicks = (max: number): number[] => {
  if (max <= 100) {
    const step = 20;
    return Array.from({ length: Math.ceil(max / step) + 1 }, (_, i) => i * step);
  } else if (max <= 300) {
    const step = 50;
    return Array.from({ length: Math.ceil(max / step) + 1 }, (_, i) => i * step);
  } else if (max <= 600) {
    const step = 100;
    return Array.from({ length: Math.ceil(max / step) + 1 }, (_, i) => i * step);
  } else {
    const step = 200;
    return Array.from({ length: Math.ceil(max / step) + 1 }, (_, i) => i * step);
  }
};

const CRMPiekanalyseDetail: React.FC = () => {
  const navigate = useNavigate();
  const { analyseId } = useParams<{ analyseId: string }>();
  const { deleteAnalysis } = usePiekanalyses();
  const chartsRef = useRef<HTMLDivElement>(null);
  
  // Day viewer state
  const [dayViewerOpen, setDayViewerOpen] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [initialCompareDate, setInitialCompareDate] = useState<string | undefined>();
  const [selectedDayForViewer, setSelectedDayForViewer] = useState<string | undefined>();
  
  // Target solution state - persisted per analysis
  const [targetSolutionKWh, setTargetSolutionKWh] = useState<number>(() => {
    if (!analyseId) return 0;
    const saved = localStorage.getItem(`piek-target-${analyseId}`);
    return saved ? parseInt(saved) : 0;
  });
  
  // Save target solution to localStorage when changed
  useEffect(() => {
    if (analyseId) {
      localStorage.setItem(`piek-target-${analyseId}`, String(targetSolutionKWh));
    }
  }, [targetSolutionKWh, analyseId]);

  const { data: analysis, isLoading, error } = useQuery({
    queryKey: ['piek-analyse', analyseId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('piek_analyses')
        .select('*')
        .eq('id', analyseId)
        .single();
      
      if (error) throw error;
      return data as unknown as PiekAnalyse;
    },
    enabled: !!analyseId,
  });

  // Calculate global max for consistent Y-axis (rounded up to nearest 50)
  const globalMax = useMemo(() => {
    if (!analysis) return 300;
    
    const yearOverview = analysis.year_overview || [];
    const highestDay = analysis.highest_day?.hourlyData || [];
    const monthlyOverviews = analysis.monthly_overviews || {};
    
    // Get max from all data sources
    const allValues = [
      analysis.highest_peak_kwp || 0,
      analysis.config?.contractedCapacity || 0,
      ...yearOverview.map((d: any) => d.maxKWp || 0),
      ...yearOverview.map((d: any) => d.maxReturnKWp || 0),
      ...highestDay.map((d: any) => d.maxKWp || 0),
      ...highestDay.map((d: any) => d.maxReturnKWp || 0),
      ...Object.values(monthlyOverviews).flat().map((d: any) => d.maxKWp || 0),
      ...Object.values(monthlyOverviews).flat().map((d: any) => d.maxReturnKWp || 0),
    ];
    
    const maxValue = Math.max(...allValues) * 1.15; // 15% padding
    return Math.ceil(maxValue / 50) * 50; // Round up to nearest 50
  }, [analysis]);

  const yTicks = useMemo(() => generateYTicks(globalMax), [globalMax]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
        <p className="font-semibold text-foreground mb-2">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="font-medium">{entry.value?.toFixed(1)} kWp</span>
          </p>
        ))}
      </div>
    );
  };

  const handleExportPNG = async (elementId: string, filename: string) => {
    const element = document.getElementById(elementId);
    if (!element) return;

    try {
      // Clone element for 1500x1500px square export
      const clone = element.cloneNode(true) as HTMLElement;
      clone.style.cssText = `
        position: absolute;
        left: -9999px;
        width: 1500px;
        height: 1500px;
        padding: 24px;
        background-color: #ffffff;
        border-radius: 12px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
      `;
      
      // Make Card fullscreen with flex
      clone.querySelectorAll('.rounded-lg').forEach(el => {
        (el as HTMLElement).style.cssText = 'width: 100% !important; height: 100% !important; display: flex !important; flex-direction: column !important; flex: 1 !important;';
      });
      
      // CardContent must flex-grow
      clone.querySelectorAll('.pt-0, .p-6').forEach(el => {
        (el as HTMLElement).style.cssText = 'flex: 1 !important; display: flex !important; flex-direction: column !important; padding: 16px !important;';
      });
      
      // Force chart container divs to fill all available space
      clone.querySelectorAll('.h-48, .h-80').forEach(el => {
        (el as HTMLElement).style.cssText = 'flex: 1 !important; width: 100% !important; height: 100% !important; min-height: 1300px !important;';
      });
      
      // Force ResponsiveContainer to fill space
      clone.querySelectorAll('.recharts-responsive-container').forEach(el => {
        (el as HTMLElement).style.cssText = 'width: 100% !important; height: 100% !important; min-height: 1300px !important;';
      });
      
      // Force recharts-wrapper and SVG to scale
      clone.querySelectorAll('.recharts-wrapper').forEach(el => {
        (el as HTMLElement).style.cssText = 'width: 100% !important; height: 100% !important;';
        (el as HTMLElement).setAttribute('width', '1400');
        (el as HTMLElement).setAttribute('height', '1300');
      });
      clone.querySelectorAll('svg.recharts-surface').forEach(el => {
        (el as SVGElement).setAttribute('width', '1400');
        (el as SVGElement).setAttribute('height', '1300');
        (el as SVGElement).style.cssText = 'width: 100% !important; height: 100% !important;';
      });
      
      // Compact title styling
      const title = clone.querySelector('.text-sm.font-semibold, h3, .font-semibold');
      if (title) {
        (title as HTMLElement).style.cssText = 'font-size: 32px !important; font-weight: 700 !important; margin-bottom: 16px !important; flex-shrink: 0 !important;';
      }
      
      // Hide export buttons in clone
      clone.querySelectorAll('[data-export-hide]').forEach(el => {
        (el as HTMLElement).style.display = 'none';
      });
      
      document.body.appendChild(clone);
      
      // Wait longer for Recharts to fully re-render at new dimensions
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const canvas = await html2canvas(clone, { 
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
        width: 1500,
        height: 1500,
      });
      
      document.body.removeChild(clone);
      
      // Blob-based download with max quality
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `${filename}.png`;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png', 1.0);
      
      toast.success('Grafiek geëxporteerd (1500x1500)');
    } catch (error) {
      toast.error('Export mislukt');
    }
  };

  const handleExportZip = async () => {
    if (!chartsRef.current) return;

    try {
      toast.info('1500x1500 Export wordt voorbereid...');
      const zip = new JSZip();
      const charts = chartsRef.current.querySelectorAll('[data-chart]');
      
      // Sequential processing for stability
      for (const chart of Array.from(charts)) {
        const name = chart.getAttribute('data-chart');
        
        // Clone element with 1500x1500px square dimensions
        const clone = chart.cloneNode(true) as HTMLElement;
        clone.style.cssText = `
          position: absolute;
          left: -9999px;
          width: 1500px;
          height: 1500px;
          padding: 24px;
          background-color: #ffffff;
          border-radius: 12px;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
        `;
        
        // Make Card fullscreen with flex
        clone.querySelectorAll('.rounded-lg').forEach(el => {
          (el as HTMLElement).style.cssText = 'width: 100% !important; height: 100% !important; display: flex !important; flex-direction: column !important; flex: 1 !important;';
        });
        
        // CardContent must flex-grow
        clone.querySelectorAll('.pt-0, .p-6').forEach(el => {
          (el as HTMLElement).style.cssText = 'flex: 1 !important; display: flex !important; flex-direction: column !important; padding: 16px !important;';
        });
        
        // Force chart container divs to fill all available space
        clone.querySelectorAll('.h-48, .h-80').forEach(el => {
          (el as HTMLElement).style.cssText = 'flex: 1 !important; width: 100% !important; height: 100% !important; min-height: 1300px !important;';
        });
        
        // Force ResponsiveContainer to fill space
        clone.querySelectorAll('.recharts-responsive-container').forEach(el => {
          (el as HTMLElement).style.cssText = 'width: 100% !important; height: 100% !important; min-height: 1300px !important;';
        });
        
        // Force recharts-wrapper and SVG to scale
        clone.querySelectorAll('.recharts-wrapper').forEach(el => {
          (el as HTMLElement).style.cssText = 'width: 100% !important; height: 100% !important;';
          (el as HTMLElement).setAttribute('width', '1400');
          (el as HTMLElement).setAttribute('height', '1300');
        });
        clone.querySelectorAll('svg.recharts-surface').forEach(el => {
          (el as SVGElement).setAttribute('width', '1400');
          (el as SVGElement).setAttribute('height', '1300');
          (el as SVGElement).style.cssText = 'width: 100% !important; height: 100% !important;';
        });
        
        // Compact title styling
        const title = clone.querySelector('.text-sm.font-semibold, h3, .font-semibold');
        if (title) {
          (title as HTMLElement).style.cssText = 'font-size: 32px !important; font-weight: 700 !important; margin-bottom: 16px !important; flex-shrink: 0 !important;';
        }
        
        // Hide export buttons
        clone.querySelectorAll('[data-export-hide]').forEach(el => {
          (el as HTMLElement).style.display = 'none';
        });
        
        document.body.appendChild(clone);
        
        // Wait longer for Recharts to fully re-render
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const canvas = await html2canvas(clone, { 
          scale: 2,
          backgroundColor: '#ffffff',
          logging: false,
          width: 1500,
          height: 1500,
        });
        
        document.body.removeChild(clone);
        
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob(b => resolve(b!), 'image/png', 1.0);
        });
        
        zip.file(`${name}.png`, blob);
      }
      
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.download = `${analysis?.name || 'analyse'}_1500x1500_export.zip`;
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Alle grafieken geëxporteerd (1500x1500)');
    } catch (error) {
      toast.error('ZIP export mislukt');
    }
  };

  const handleExportConsumptionPDF = async () => {
    if (!analysis) return;
    try {
      await generateConsumptionReportPDF(analysis, targetSolutionKWh);
      toast.success('Verbruik PDF geëxporteerd');
    } catch (error) {
      toast.error('PDF export mislukt: ' + (error as Error).message);
    }
  };

  const handleDelete = async () => {
    if (!analyseId) return;
    await deleteAnalysis.mutateAsync(analyseId);
    navigate('/crm/analyses');
  };

  // Handle day click from calendar
  const handleDayClick = (date: string) => {
    setSelectedDayForViewer(date);
    setDayViewerOpen(true);
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div>
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48 mt-2" />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-80" />
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate('/crm/analyses')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Terug
        </Button>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Analyse niet gevonden</p>
        </div>
      </div>
    );
  }

  const config = analysis.config || { dataType: 'kWp', includeReturnDelivery: false, contractedCapacity: 235 };
  const yearOverview = analysis.year_overview || [];
  const monthlyOverviews = analysis.monthly_overviews || {};
  const highestDay = analysis.highest_day || { date: new Date().toISOString(), hourlyData: [] };

  // Shared chart styling
  const axisStyle = { 
    fontSize: 12, 
    fill: '#4b5563',
    fontWeight: 500,
  };
  
  const gridStyle = {
    strokeDasharray: '3 3',
    stroke: '#d1d5db',
    strokeOpacity: 0.7,
    vertical: true,
    horizontal: true,
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/crm/analyses')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold">{analysis.name}</h1>
            <Badge variant="outline">
              <Zap className="h-3 w-3 mr-1" />
              Piekvermogen
            </Badge>
            {analysis.analysis_year && (
              <Badge variant="secondary">
                <Calendar className="h-3 w-3 mr-1" />
                {analysis.analysis_year}
              </Badge>
            )}
            {analysis.organization_name && (
              <Badge variant="outline" className="bg-muted">
                <Building2 className="h-3 w-3 mr-1" />
                {analysis.organization_name}
              </Badge>
            )}
            {!analysis.raw_data && (
              <Badge variant="outline" className="text-muted-foreground">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Beperkte weergave
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            Aangemaakt op {format(new Date(analysis.created_at), 'd MMMM yyyy', { locale: nl })}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {analysis.raw_data && (
            <>
              <Button variant="outline" onClick={() => setDayViewerOpen(true)}>
                <CalendarDays className="h-4 w-4 mr-2" />
                Dag Verkenner
              </Button>
              <Button variant="outline" onClick={handleExportConsumptionPDF}>
                <FileText className="h-4 w-4 mr-2" />
                Verbruik PDF
              </Button>
            </>
          )}
          <Button variant="outline" onClick={handleExportZip}>
            <Download className="h-4 w-4 mr-2" />
            ZIP Export
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4 mr-2" />
            Verwijderen
          </Button>
        </div>
      </div>

      <div ref={chartsRef} className="space-y-6">
        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" id="stats" data-chart="statistieken">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <TrendingUp className="h-4 w-4" />
                <span className="text-sm">Hoogste Piek</span>
              </div>
              <p className="text-2xl font-bold">{analysis.highest_peak_kwp} kWp</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Zap className="h-4 w-4" />
                <span className="text-sm">Contract</span>
              </div>
              <p className="text-2xl font-bold">{config.contractedCapacity} kWp</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Calendar className="h-4 w-4" />
                <span className="text-sm">Maanden Boven</span>
              </div>
              <p className="text-2xl font-bold">{analysis.months_above_contract}/12</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm">Overschrijdingen</span>
              </div>
              <p className="text-2xl font-bold">{analysis.total_exceedances}</p>
            </CardContent>
          </Card>
        </div>

        {/* Info Badges */}
        {(config.dataType === 'kWh' || analysis.has_return_delivery) && (
          <div className="flex gap-2 flex-wrap">
            {config.dataType === 'kWh' && (
              <Alert className="w-auto">
                <Info className="h-4 w-4" />
                <AlertDescription>kWh data geconverteerd naar kWp</AlertDescription>
              </Alert>
            )}
            {analysis.has_return_delivery && (
              <Alert className="w-auto">
                <Info className="h-4 w-4" />
                <AlertDescription>Teruglevering data aanwezig</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Year Overview Chart */}
        <Card id="yearChart" data-chart="jaaroverzicht">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Jaaroverzicht
              </CardTitle>
              <CardDescription>Maximale piek per maand</CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleExportPNG('yearChart', 'jaaroverzicht')}
              data-export-hide
            >
              <Download className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={yearOverview} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                  <CartesianGrid {...gridStyle} syncWithTicks={true} />
                  <XAxis 
                    dataKey="monthName" 
                    tick={axisStyle}
                    axisLine={{ stroke: '#9ca3af' }}
                    tickLine={{ stroke: '#9ca3af' }}
                    interval={0}
                    height={35}
                    tickFormatter={(value) => {
                      const index = DUTCH_MONTHS.indexOf(value);
                      return index !== -1 ? DUTCH_MONTH_ABBR[index] : value;
                    }}
                  />
                  <YAxis 
                    domain={[0, globalMax]} 
                    ticks={yTicks}
                    tick={axisStyle}
                    axisLine={{ stroke: '#9ca3af' }}
                    tickLine={{ stroke: '#9ca3af' }}
                    label={{ 
                      value: 'kWp', 
                      angle: -90, 
                      position: 'insideLeft',
                      offset: -5,
                      style: { textAnchor: 'middle', fill: '#6b7280', fontSize: 12, fontWeight: 600 } 
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ paddingTop: 10 }} />
                  <ReferenceLine 
                    y={config.contractedCapacity} 
                    stroke="hsl(var(--destructive))" 
                    strokeDasharray="5 5" 
                    strokeWidth={2}
                    label={{ 
                      value: `Contract ${config.contractedCapacity} kWp`, 
                      position: 'right', 
                      fill: 'hsl(var(--destructive))',
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="maxKWp" 
                    stroke="#014336" 
                    strokeWidth={2.5}
                    name="Verbruik"
                    dot={{ fill: '#014336', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                  />
                  {analysis.has_return_delivery && (
                    <Line 
                      type="monotone" 
                      dataKey="maxReturnKWp" 
                      stroke="#FF8C00" 
                      strokeWidth={2.5}
                      name="Teruglevering"
                      dot={{ fill: '#FF8C00', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, strokeWidth: 2 }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Highest Day Chart */}
        <Card id="highestDayChart" data-chart="hoogste_dag">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Hoogste Dag - Uurwaarden
              </CardTitle>
              <CardDescription>
                {format(new Date(highestDay.date), 'd MMMM yyyy', { locale: nl })}
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleExportPNG('highestDayChart', 'hoogste_dag')}
              data-export-hide
            >
              <Download className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={highestDay.hourlyData} margin={{ top: 10, right: 30, left: 20, bottom: 10 }}>
                  <CartesianGrid {...gridStyle} syncWithTicks={true} />
                  <XAxis 
                    dataKey="hourLabel" 
                    tick={axisStyle}
                    axisLine={{ stroke: '#9ca3af' }}
                    tickLine={{ stroke: '#9ca3af' }}
                    interval={0}
                  />
                  <YAxis 
                    domain={[0, globalMax]} 
                    ticks={yTicks}
                    tick={axisStyle}
                    axisLine={{ stroke: '#9ca3af' }}
                    tickLine={{ stroke: '#9ca3af' }}
                    label={{ 
                      value: 'kWp', 
                      angle: -90, 
                      position: 'insideLeft',
                      offset: -5,
                      style: { textAnchor: 'middle', fill: '#6b7280', fontSize: 12, fontWeight: 600 } 
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ paddingTop: 10 }} />
                  <ReferenceLine 
                    y={config.contractedCapacity} 
                    stroke="hsl(var(--destructive))" 
                    strokeDasharray="5 5"
                    strokeWidth={2}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="maxKWp" 
                    stroke="#014336" 
                    strokeWidth={2.5}
                    name="Verbruik"
                    dot={{ fill: '#014336', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                  />
                  {analysis.has_return_delivery && (
                    <Line 
                      type="monotone" 
                      dataKey="maxReturnKWp" 
                      stroke="#FF8C00" 
                      strokeWidth={2.5}
                      name="Teruglevering"
                      dot={{ fill: '#FF8C00', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, strokeWidth: 2 }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Charts Grid */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Maandoverzichten</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {DUTCH_MONTHS.map((monthName, monthIndex) => {
              const monthData = monthlyOverviews[monthIndex] || [];
              if (monthData.length === 0) return null;
              
              return (
                <Card key={monthIndex} id={`month-${monthIndex}`} data-chart={`${monthName.toLowerCase()}_dagen`}>
                  <CardHeader className="py-3 flex flex-row items-center justify-between">
                    <CardTitle className="text-sm font-semibold">{monthName}</CardTitle>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleExportPNG(`month-${monthIndex}`, `${monthName.toLowerCase()}_dagen`)}
                      data-export-hide
                    >
                      <Download className="h-3 w-3" />
                    </Button>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={monthData} margin={{ top: 10, right: 15, left: 5, bottom: 15 }}>
                          <CartesianGrid 
                            strokeDasharray="3 3" 
                            stroke="#e5e7eb" 
                            strokeOpacity={0.8}
                            vertical={true}
                            horizontal={true}
                          />
                          <XAxis 
                            dataKey="dateStr" 
                            tick={{ fontSize: 11, fill: '#4b5563', fontWeight: 500 }} 
                            axisLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
                            tickLine={{ stroke: '#9ca3af' }}
                            interval={4}
                            tickMargin={8}
                            height={30}
                          />
                          <YAxis 
                            domain={[0, globalMax]} 
                            ticks={yTicks.filter((_, i) => i % 2 === 0)}
                            tick={{ fontSize: 11, fill: '#4b5563', fontWeight: 500 }}
                            axisLine={{ stroke: '#9ca3af', strokeWidth: 1 }}
                            tickLine={{ stroke: '#9ca3af' }}
                            width={50}
                            tickMargin={5}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <ReferenceLine 
                            y={config.contractedCapacity} 
                            stroke="#dc2626" 
                            strokeDasharray="6 4"
                            strokeWidth={2}
                            label={{ 
                              value: 'Limiet', 
                              position: 'insideTopRight',
                              fontSize: 10,
                              fill: '#dc2626',
                              fontWeight: 600,
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="maxKWp" 
                            stroke="#014336" 
                            strokeWidth={2}
                            dot={false}
                          />
                          {analysis.has_return_delivery && (
                            <Line 
                              type="monotone" 
                              dataKey="maxReturnKWp" 
                              stroke="#FF8C00" 
                              strokeWidth={2}
                              dot={false}
                            />
                          )}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Solution Settings & Year Calendar */}
        {analysis.raw_data && (
          <div className="space-y-6">
            <SolutionSettingsCard
              contractedCapacity={config.contractedCapacity}
              targetSolutionKWh={targetSolutionKWh}
              onTargetChange={setTargetSolutionKWh}
            />
            
            <YearCalendar
              rawData={analysis.raw_data}
              contractedCapacity={config.contractedCapacity}
              targetSolutionKWh={targetSolutionKWh}
              onDayClick={handleDayClick}
              year={analysis.analysis_year}
            />
          </div>
        )}
      </div>

      {/* Day Viewer Dialog */}
      <DayViewerDialog
        open={dayViewerOpen}
        onClose={() => {
          setDayViewerOpen(false);
          setSelectedDayForViewer(undefined);
        }}
        rawData={analysis.raw_data}
        config={config}
        targetSolutionKWh={targetSolutionKWh}
        initialDate={selectedDayForViewer}
        onCompare={(date) => {
          setInitialCompareDate(date);
          setDayViewerOpen(false);
          setCompareDialogOpen(true);
        }}
      />

      {/* Day Compare Dialog */}
      <DayCompareDialog
        open={compareDialogOpen}
        onClose={() => setCompareDialogOpen(false)}
        rawData={analysis.raw_data}
        config={config}
        initialDateA={initialCompareDate}
      />
    </div>
  );
};

export default CRMPiekanalyseDetail;
